import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import useDriverHeartbeat from "@/hooks/useDriverHeartbeat";
import { useTheme } from "@/context/ThemeContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import {
  LayoutDashboard, Pill, Truck, Map, ClipboardList, Users, LogOut, PackagePlus, Route as RouteIcon, UserCircle2, BarChart3, UserRound, Sun, Moon, Building2, ScanLine, Settings, ChevronDown, FileText, Loader2, ScrollText, Clock3, MapPin, ShieldCheck, Calculator, DollarSign,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { compressLogo } from "@/lib/imageCompress";
import { getDLToken } from "@/lib/dlToken";
import { useRegion, REGIONS, ALL_REGION } from "@/context/RegionContext";
import { toast } from "sonner";

// Each role has `primary` (always-on tabs in the bar) plus an optional `manage`
// dropdown of less-frequent management screens. Keeps the top bar to ~6 items
// so nothing wraps or gets clipped on laptops/tablets.
const ROLE_NAV = {
  super_admin: {
    primary: [
      { to: "/admin", label: "Overview", icon: LayoutDashboard },
      { to: "/admin/orders", label: "Orders", icon: ClipboardList },
      { to: "/admin/routes", label: "Routes", icon: RouteIcon },
      { to: "/scan", label: "Scan", icon: ScanLine },
      { to: "/admin/reports", label: "Reports", icon: BarChart3 },
    ],
    manage: [
      { to: "/admin/pharmacies", label: "Pharmacies", icon: Pill },
      { to: "/admin/patients", label: "Patients", icon: UserRound },
      { to: "/admin/users", label: "Users", icon: Users },
      { to: "/admin/company", label: "My Company", icon: Building2 },
      { to: "/admin/invoices", label: "Invoices", icon: FileText },
      { to: "/admin/copay-summary", label: "Copay Summary", icon: DollarSign },
      { to: "/admin/accounting", label: "Accounting", icon: Calculator },
      { to: "/admin/timesheet", label: "TimeSheet", icon: Clock3 },
      { to: "/admin/hipaa", label: "HIPAA", icon: ShieldCheck },
      { to: "/admin/audit-log", label: "Audit Log", icon: ScrollText },
    ],
  },
  dispatcher: {
    primary: [
      { to: "/dispatch", label: "Overview", icon: LayoutDashboard },
      { to: "/dispatch/orders", label: "Orders", icon: ClipboardList },
      { to: "/dispatch/routes", label: "Routes", icon: RouteIcon },
      { to: "/scan", label: "Scan", icon: ScanLine },
    ],
    manage: [
      { to: "/dispatch/drivers", label: "Drivers", icon: Truck },
      { to: "/dispatch/copay-summary", label: "Copay Summary", icon: DollarSign },
      { to: "/dispatch/timesheet", label: "TimeSheet", icon: Clock3 },
      { to: "/dispatch/hipaa", label: "HIPAA", icon: ShieldCheck },
      { to: "/dispatch/audit-log", label: "Audit Log", icon: ScrollText },
    ],
  },
  pharmacy_staff: {
    primary: [
      { to: "/staff", label: "Overview", icon: LayoutDashboard },
      { to: "/staff/orders", label: "My Orders", icon: ClipboardList },
      { to: "/staff/new", label: "New Order", icon: PackagePlus },
      { to: "/staff/patients", label: "Patients", icon: UserRound },
      { to: "/staff/invoices", label: "Invoices", icon: FileText },
      { to: "/staff/copay-summary", label: "Copay Summary", icon: DollarSign },
      { to: "/staff/hipaa", label: "HIPAA", icon: ShieldCheck },
      { to: "/scan", label: "Scan", icon: ScanLine },
    ],
    manage: [],
  },
  driver: {
    primary: [
      { to: "/driver", label: "Home", icon: LayoutDashboard },
      { to: "/driver/routes", label: "My Routes", icon: Map },
      { to: "/scan", label: "Scan", icon: ScanLine },
    ],
    manage: [],
  },
};

const ROLE_LABEL = {
  super_admin: "Super Admin",
  dispatcher: "Dispatcher",
  pharmacy_staff: "Pharmacy Staff",
  driver: "Driver",
};

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Drivers stay "online" automatically while a route of theirs is active
  // (in_progress / paused). This hook polls the backend every 30 s and
  // fires the 60 s heartbeat + geolocation watch only when needed.
  useDriverHeartbeat(user?.role === "driver");

  // Company logo lives on the company document — fetched once at mount and
  // refreshed whenever the super admin uploads a new one via the brand square.
  const [companyLogoUrl, setCompanyLogoUrl] = useState(null);
  const [logoBusy, setLogoBusy] = useState(false);
  const logoInputRef = useRef(null);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    api.get("/company").then(({ data }) => {
      if (!cancelled) setCompanyLogoUrl(data?.logo_url || null);
    }).catch(() => { /* anonymous routes / company missing → fallback to + icon */ });
    return () => { cancelled = true; };
  }, [user]);

  const canEditLogo = user?.role === "super_admin";
  const onPickLogo = () => {
    if (!canEditLogo || logoBusy) return;
    logoInputRef.current?.click();
  };
  const onLogoFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setLogoBusy(true);
    try {
      const compressed = await compressLogo(f);
      const fd = new FormData();
      fd.append("logo", compressed);
      const { data } = await api.post("/company/logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setCompanyLogoUrl(data.logo_url);
      toast.success("Company logo updated");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLogoBusy(false);
    }
  };

  if (!user) return null;
  const nav = ROLE_NAV[user.role] || { primary: [], manage: [] };
  const manageActive = nav.manage.some((m) => location.pathname.startsWith(m.to));
  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const logoSrc = companyLogoUrl
    ? `${BACKEND_URL}${companyLogoUrl}?auth=${encodeURIComponent(getDLToken())}`
    : null;

  return (
    <div className="min-h-screen flex flex-col" data-testid="app-layout">
      <header className="sticky top-0 z-40 border-b border-border bg-card/85 backdrop-blur-md" data-testid="topbar">
        <div className="px-4 md:px-8 py-2 flex items-center gap-4">
          {/* Brand — clickable logo square (super_admin can replace it inline) */}
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={onPickLogo}
              disabled={!canEditLogo}
              className={`relative h-8 w-8 rounded-lg grid place-items-center font-display font-bold overflow-hidden transition-all ${
                logoSrc
                  ? "bg-card border border-border"
                  : "bg-primary text-primary-foreground"
              } ${canEditLogo ? "hover:ring-2 hover:ring-primary/40 cursor-pointer" : "cursor-default"}`}
              title={canEditLogo ? (logoSrc ? "Click to replace company logo" : "Click to upload company logo") : "Company logo"}
              data-testid="brand-logo-button"
              aria-label="Company logo"
            >
              {logoSrc ? (
                <img src={logoSrc} alt="Company logo" className="h-full w-full object-contain" />
              ) : (
                <span aria-hidden="true">+</span>
              )}
              {logoBusy && (
                <span className="absolute inset-0 grid place-items-center bg-black/55">
                  <Loader2 className="h-4 w-4 text-white animate-spin" />
                </span>
              )}
            </button>
            <input
              ref={logoInputRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onLogoFile}
              data-testid="brand-logo-input"
            />
            <span className="font-display font-bold leading-none hidden lg:block">SRCI Pharm-Route</span>
          </div>
          {/* Tabs */}
          <nav className="flex-1 min-w-0 overflow-x-auto scrollbar-thin">
            <div className="flex items-center justify-center gap-1">
              {nav.primary.map((n) => (
                <NavLink
                  key={n.to}
                  to={n.to}
                  end={n.to === "/admin" || n.to === "/dispatch" || n.to === "/staff" || n.to === "/admin/orders" || n.to === "/driver"}
                  data-testid={`nav-${n.to.replace(/\//g, "-")}`}
                  className={({ isActive }) =>
                    `inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                      isActive
                        ? "bg-primary text-primary-foreground"
                        : "text-foreground/75 hover:bg-secondary hover:text-foreground"
                    }`
                  }
                >
                  <n.icon className="h-3.5 w-3.5" />
                  {n.label}
                </NavLink>
              ))}

              {nav.manage.length > 0 && (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <button
                      type="button"
                      data-testid="nav-manage"
                      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full px-3 py-1.5 text-sm transition-colors ${
                        manageActive
                          ? "bg-primary text-primary-foreground"
                          : "text-foreground/75 hover:bg-secondary hover:text-foreground"
                      }`}
                    >
                      <Settings className="h-3.5 w-3.5" />
                      Manage
                      <ChevronDown className="h-3.5 w-3.5 opacity-60" />
                    </button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56" data-testid="nav-manage-menu">
                    {nav.manage.map((m) => (
                      <DropdownMenuItem
                        key={m.to}
                        onClick={() => navigate(m.to)}
                        className="cursor-pointer"
                        data-testid={`nav-manage-${m.to.replace(/\//g, "-")}`}
                      >
                        <m.icon className="h-4 w-4 mr-2 text-primary" />
                        {m.label}
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              )}
            </div>
          </nav>
          {/* User + actions */}
          <div className="hidden md:flex items-center gap-2 shrink-0">
            <RegionSwitcher />
            <div className="flex items-center gap-2 px-2 py-1 rounded-lg bg-secondary/60">
              <UserCircle2 className="h-5 w-5 text-primary" />
              <div className="leading-tight">
                <div className="text-xs font-medium" data-testid="current-user-name">{user.name}</div>
                <div className="text-[10px] text-muted-foreground">{ROLE_LABEL[user.role]}</div>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle" title={theme === "dark" ? "Light theme" : "Dark theme"}>
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn" title="Sign out">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
          {/* Mobile actions */}
          <div className="md:hidden flex items-center gap-1 shrink-0">
            <Button variant="ghost" size="icon" onClick={toggle} data-testid="theme-toggle-mobile">
              {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>
            <Button variant="ghost" size="icon" onClick={() => { logout(); navigate("/login"); }} data-testid="logout-btn-mobile">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <main className="flex-1 min-w-0">{children}</main>
    </div>
  );
}

function RegionSwitcher() {
  const { selected, setRegion, canSwitch, active } = useRegion();
  // Non super_admin users see a small read-only badge showing which workspace
  // their account belongs to. Super Admins get an interactive dropdown.
  if (!canSwitch) {
    return (
      <div
        className="hidden md:flex items-center gap-1.5 px-2 py-1 rounded-lg bg-secondary/60 text-xs"
        title={`Workspace: ${active.label}`}
        data-testid="region-badge"
      >
        <MapPin className="h-3.5 w-3.5 text-primary" />
        <span className="font-medium">{active.short}</span>
      </div>
    );
  }
  const items = [ALL_REGION, ...REGIONS];
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="h-8 rounded-lg px-2.5 gap-1.5 bg-secondary/60 hover:bg-secondary text-xs"
          data-testid="region-switcher-trigger"
          title="Switch workspace"
        >
          <MapPin className="h-3.5 w-3.5 text-primary" />
          <span className="font-medium">{active.short}</span>
          <ChevronDown className="h-3 w-3 opacity-70" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" data-testid="region-switcher-menu">
        {items.map((r) => (
          <DropdownMenuItem
            key={r.id}
            onClick={() => {
              setRegion(r.id);
              // Force a full page reload so every currently-mounted query
              // re-fetches with the new region param. Cheaper than a
              // manual invalidate-and-refetch of every hook.
              window.location.reload();
            }}
            data-testid={`region-switcher-item-${r.id}`}
            className={selected === r.id ? "font-semibold" : ""}
          >
            {selected === r.id ? "● " : "  "}{r.label}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
